import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { subscriptionApi } from "../services/api";
import type { StripeCardElementOptions } from "@stripe/stripe-js";

interface PaymentCardFormProps {
    onSuccess: (setupIntent: any) => void;
    onError: (error: string) => void;
    onCancel?: () => void;
}

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
    style: {
        base: {
            color: "#32325d",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
                color: "#aab7c4",
            },
        },
        invalid: {
            color: "#fa755a",
            iconColor: "#fa755a",
        },
    },
    // Hide the postal code field - we'll collect it separately to support international addresses
    hidePostalCode: true,
};

export const PaymentCardForm: React.FC<PaymentCardFormProps> = ({
    onSuccess,
    onError,
    onCancel,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [postalCode, setPostalCode] = useState("");

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setIsProcessing(true);
        setCardError(null);

        try {
            // Get the client secret from the backend
            const { client_secret } = await subscriptionApi.createSetupIntent();

            // Confirm the setup intent with postal code
            const result = await stripe.confirmCardSetup(client_secret, {
                payment_method: {
                    card: elements.getElement(CardElement)!,
                    billing_details: {
                        address: {
                            postal_code: postalCode,
                        },
                    },
                },
            });

            if (result.error) {
                setCardError(result.error.message || "An error occurred");
                onError(result.error.message || "An error occurred");
            } else {
                // Payment method successfully attached
                onSuccess(result.setupIntent);
            }
        } catch (error: any) {
            const message =
                error.response?.data?.detail ||
                error.message ||
                "Failed to set up payment method";
            setCardError(message);
            onError(message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-4 border rounded-md">
                <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>

            <div>
                <label
                    htmlFor="postal-code"
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    Postal / ZIP Code
                </label>
                <input
                    id="postal-code"
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="e.g., 12345 or K1A 0B1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                />
                <p className="mt-1 text-xs text-gray-500">
                    Enter your billing postal code (supports international
                    formats)
                </p>
            </div>

            {cardError && (
                <div className="text-red-600 text-sm">{cardError}</div>
            )}

            <div className="flex gap-4">
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isProcessing}
                        className="flex-1 py-2 px-4 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Back
                    </button>
                )}
                <button
                    type="submit"
                    disabled={!stripe || isProcessing}
                    className={`flex-1 py-2 px-4 rounded-md font-medium text-white transition-colors ${
                        isProcessing || !stripe
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >
                    {isProcessing ? "Processing..." : "Save Payment Method"}
                </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
                Your payment information is securely processed by Stripe. We
                never store your card details.
            </p>
        </form>
    );
};
